import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Code, 
  Copy, 
  Check, 
  Globe, 
  Smartphone,
  Layout,
  BarChart3,
  CheckCircle2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const WidgetGuide = () => {
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = async (code, type) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(type);
      toast.success('Code copied!');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const CodeBlock = ({ code, type, title }) => (
    <div className="relative mt-3">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-slate-600">{title}</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => copyToClipboard(code, type)}
          className="h-8"
        >
          {copiedCode === type ? (
            <><Check className="h-4 w-4 mr-1 text-green-500" /> Copied!</>
          ) : (
            <><Copy className="h-4 w-4 mr-1" /> Copy</>
          )}
        </Button>
      </div>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-3">
          <div className="p-3 bg-teal-100 rounded-xl">
            <Code className="h-8 w-8 text-teal-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">
            Embed Widget Guide
          </h1>
        </div>
        <p className="text-slate-600 max-w-2xl mx-auto">
          Learn how to add your membership plans to any external website
        </p>
      </div>

      {/* What is the Widget */}
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-teal-600" />
            What is the MyMedMember Widget?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-700">
            The embeddable widget allows you to display your membership plans on <strong>any external website</strong> 
            (WordPress, Wix, Squarespace, landing pages, etc.) without special plugins.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Responsive</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Multi-language</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Analytics included</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span>Fast loading</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step by Step */}
      <div className="space-y-6">
        {/* Step 1 */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-600 text-white font-bold">1</span>
              <CardTitle>Get the Widget Code</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-2 text-slate-700">
              <li>Log in to your <strong>Admin Dashboard</strong></li>
              <li>Go to <strong>Settings</strong> in the sidebar menu</li>
              <li>Click on the <strong>"Embed Widget"</strong> tab</li>
              <li>Select the widget type you need</li>
            </ol>
            <Button 
              onClick={() => navigate('/admin/settings')}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Go to Settings → Embed Widget
            </Button>
          </CardContent>
        </Card>

        {/* Step 2 */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-600 text-white font-bold">2</span>
              <CardTitle>Copy the Code</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all-plans" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all-plans">All Plans</TabsTrigger>
                <TabsTrigger value="single">Single Plan</TabsTrigger>
                <TabsTrigger value="iframe">iFrame</TabsTrigger>
              </TabsList>

              <TabsContent value="all-plans" className="space-y-4">
                <div className="flex items-center gap-2 mt-4">
                  <Badge className="bg-teal-100 text-teal-700">Recommended</Badge>
                  <span className="text-sm text-slate-600">Displays all your active plans</span>
                </div>
                
                <CodeBlock
                  code={`<!-- MyMedMember Widget - All Plans -->
<div data-medmember-widget data-lang="en"></div>
<script src="https://www.mymedmember.com/api/embed/widget/YOUR-CLINIC-SLUG.js" async></script>`}
                  type="all-plans"
                  title="English Code"
                />

                <CodeBlock
                  code={`<!-- MyMedMember Widget - All Plans (Spanish) -->
<div data-medmember-widget data-lang="es"></div>
<script src="https://www.mymedmember.com/api/embed/widget/YOUR-CLINIC-SLUG.js" async></script>`}
                  type="all-plans-es"
                  title="Spanish Code"
                />

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Important</p>
                      <p className="text-sm text-amber-700">
                        Replace <code className="bg-amber-100 px-1 rounded">YOUR-CLINIC-SLUG</code> with your clinic's slug. 
                        Find it in Settings → Embed Widget.
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="single" className="space-y-4">
                <div className="flex items-center gap-2 mt-4">
                  <Badge className="bg-purple-100 text-purple-700">Landing Pages</Badge>
                  <span className="text-sm text-slate-600">Ideal for marketing campaigns</span>
                </div>

                <CodeBlock
                  code={`<!-- MyMedMember Widget - Single Plan -->
<div data-medmember-widget data-plan="PLAN-ID" data-lang="en"></div>
<script src="https://www.mymedmember.com/api/embed/widget/YOUR-CLINIC-SLUG.js" async></script>`}
                  type="single-plan"
                  title="Single Plan Code"
                />

                <p className="text-sm text-slate-600">
                  Find the <code className="bg-slate-100 px-1 rounded">PLAN-ID</code> in 
                  Settings → Embed Widget → "Landing Page" tab.
                </p>
              </TabsContent>

              <TabsContent value="iframe" className="space-y-4">
                <div className="flex items-center gap-2 mt-4">
                  <Badge variant="outline">Simple Alternative</Badge>
                  <span className="text-sm text-slate-600">For sites with script restrictions</span>
                </div>

                <CodeBlock
                  code={`<iframe 
    src="https://www.mymedmember.com/join/YOUR-CLINIC-SLUG" 
    width="100%" 
    height="800" 
    frameborder="0"
    style="border: none; max-width: 1200px;">
</iframe>`}
                  type="iframe"
                  title="iFrame Code"
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Step 3 */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-600 text-white font-bold">3</span>
              <CardTitle>Add to Your Website</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="wordpress" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="wordpress">WordPress</TabsTrigger>
                <TabsTrigger value="wix">Wix</TabsTrigger>
                <TabsTrigger value="squarespace">Squarespace</TabsTrigger>
                <TabsTrigger value="html">HTML</TabsTrigger>
              </TabsList>

              <TabsContent value="wordpress" className="mt-4">
                <ol className="list-decimal list-inside space-y-3 text-slate-700">
                  <li>Go to the page where you want to display the plans</li>
                  <li>Add a <strong>"Custom HTML"</strong> block</li>
                  <li>Paste the widget code</li>
                  <li>Save and publish the page</li>
                </ol>
              </TabsContent>

              <TabsContent value="wix" className="mt-4">
                <ol className="list-decimal list-inside space-y-3 text-slate-700">
                  <li>Open the Wix Editor</li>
                  <li>Click <strong>"Add" → "Embed" → "HTML iframe"</strong></li>
                  <li>Select <strong>"Enter Code"</strong></li>
                  <li>Paste the widget code</li>
                  <li>Adjust the container size</li>
                  <li>Publish your site</li>
                </ol>
              </TabsContent>

              <TabsContent value="squarespace" className="mt-4">
                <ol className="list-decimal list-inside space-y-3 text-slate-700">
                  <li>Edit the desired page</li>
                  <li>Add a <strong>"Code"</strong> or <strong>"Embed"</strong> block</li>
                  <li>Paste the widget code</li>
                  <li>Save your changes</li>
                </ol>
              </TabsContent>

              <TabsContent value="html" className="mt-4">
                <p className="text-slate-700 mb-3">
                  Paste the code inside the <code className="bg-slate-100 px-1 rounded">&lt;body&gt;</code> of your HTML:
                </p>
                <CodeBlock
                  code={`<!DOCTYPE html>
<html>
<head>
    <title>Our Memberships</title>
</head>
<body>
    <h1>Join Our Membership Program</h1>
    
    <!-- MyMedMember Widget -->
    <div data-medmember-widget data-lang="en"></div>
    <script src="https://www.mymedmember.com/api/embed/widget/YOUR-CLINIC-SLUG.js" async></script>
    
</body>
</html>`}
                  type="html-full"
                  title="Complete HTML Example"
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Step 4 */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-600 text-white font-bold">4</span>
              <CardTitle>View Widget Analytics</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-700">
              Once implemented, you can view real-time widget statistics:
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <BarChart3 className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Views</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <Smartphone className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Clicks</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Conversions</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg text-center">
                <Globe className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Top Sites</p>
              </div>
            </div>

            <Button 
              onClick={() => navigate('/admin/analytics')}
              variant="outline"
              className="w-full"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Go to Analytics → Widget
            </Button>
          </CardContent>
        </Card>

        {/* Verification Checklist */}
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Verification Checklist
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 mb-4">After implementation, verify that:</p>
            <div className="space-y-2">
              {[
                'The widget loads correctly',
                'Plans display with correct prices',
                'The "Join Now" button redirects to signup page',
                'The language is correct'
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-green-400 rounded flex items-center justify-center">
                    <Check className="h-3 w-3 text-green-600" />
                  </div>
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Troubleshooting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-slate-900">Widget doesn't load</h4>
                <ul className="list-disc list-inside text-sm text-slate-600 mt-1">
                  <li>Make sure the code is inside the &lt;body&gt;, not in the &lt;head&gt;</li>
                  <li>Ensure your site allows external scripts</li>
                  <li>Verify the clinic-slug is correct</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-900">Plans don't appear</h4>
                <ul className="list-disc list-inside text-sm text-slate-600 mt-1">
                  <li>Verify you have <strong>active</strong> plans in your account</li>
                  <li>Plans must be marked as active in Membership → Plans</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Support */}
      <Card className="text-center">
        <CardContent className="py-8">
          <p className="text-slate-600 mb-4">Need additional help?</p>
          <Button 
            variant="outline"
            onClick={() => window.location.href = 'mailto:support@mymedmember.com?subject=Widget%20Integration%20Support'}
          >
            Contact Support
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default WidgetGuide;
